import { Injectable } from "@angular/core";
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Store } from "@ngxs/store";
import * as _ from "lodash";
import { BehaviorSubject, firstValueFrom, Subject, take } from "rxjs";
import { RDMClientService } from "src/app/shared-resources/services/rdm-client.service";
import { BusinessRuleIndicatorItem } from "../../interface/ui-intelligencemgmt.interface";
import { RiskManagementTableService } from "../../services/risk-management.service";
import { CreateProfileState } from "../../store/ui-intelligencemgmt.states";
import { RefValue } from "@shared-interfaces/rdm-client.interface";
import { DefaultCompanyService } from "src/app/layout/services/default-company.service";

export type IndicatorType = "ics" | "ums" | "ExpressCourier";
@Injectable({ providedIn: "root" })

export class ProfileManagmentModel {
  formFields: UntypedFormGroup;
  profileId: any = "";
  detailsFormGroup: UntypedFormGroup;
  listofSelectedIndicators: string[] = [];
  listOfIndicators: any[] = [];
  listOfAllIndicators: any[] = [];
  buisnessRuleValue: any[] = [];
  department: any = [];
  selectedAeoData:any = [];
  indicatorsList: string[] = [];
  treatmentForm: UntypedFormGroup;
  schedulerForm: UntypedFormGroup;
  schemaData: any;
  newSchemaData: any = [];
  newList: any;
  modalValuePlaceHolder: string[] = [];
  private selectedIndicatorType: string;
  public profileType: string;
  public dropDownData: any[] = [];
  public inLogicalOperator: boolean = false;
  treatmentActionData: any[] = [];
  oldProfileId: any;


  //@Select(CreateProfileState.getSchema) schema$: Observable<any>;
  // @Select(CreateProfileState.getNewSchema) newSchema$: Observable<any>;
  //@Select(CreateProfileState.getIndicators) indicators$: Observable<any>;
  public schema$ = new BehaviorSubject<any>(null);
  public indicators$ = new BehaviorSubject<any>(null);
  public newSchema$ = new BehaviorSubject<any>(null);

  private formFields$ = new BehaviorSubject<any>(null);

  constructor(
    private fb: UntypedFormBuilder,
    private rdmService: RDMClientService,
    private defaultCompanyService: DefaultCompanyService,
    private clintService: RiskManagementTableService,
    private store: Store
  ) {
    this.formFields = this.fb.group({});
    this.initSchemaAndIndicators();
    this.detailsFormGroup = this.fb.group({
      detailId: [""],
      targetFieldGroup: [null, [Validators.required]],
      businessRule: this.fb.array([]),
    });

    this.treatmentForm = this.fb.group({
      treatmentType: [null ],
      treatmentItems: this.fb.array([]),
      treatmentId: null,
    });

    this.schedulerForm = this.fb.group({
      frequency: [null, [Validators.required]],
      startDate: null,
      endDate: null,
      recursive: true,
      startTime: null,
      endTime: null,
      weekList: this.fb.array([]),
      weekDayList: this.fb.array([]),
      monthStartDay: null,
      monthEndDay: null,
      schedulerId: null,
      month: null,
    });
  }

  async initSchemaAndIndicators() {
    let retryCount = 0;
    const maxRetries = 5;
    
    while (retryCount < maxRetries) {
      try {
        const [schema, indicators] = await Promise.all([
          firstValueFrom(this.store.select(CreateProfileState.getSchema).pipe(take(1))),
          firstValueFrom(this.store.select(CreateProfileState.getIndicators).pipe(take(1))),
        ]);
        
        // Check if we have valid schema data
        if (!schema || Object.keys(schema).length === 0) {
          retryCount++;
          if (retryCount >= maxRetries) {
            console.error('Schema not loaded after maximum retries');
            return;
          }
          await new Promise(resolve => setTimeout(resolve, 200));
          continue;
        }
        
        // Check if we have valid indicators data
        if (!indicators || Object.keys(indicators).length === 0) {
          retryCount++;
          if (retryCount >= maxRetries) {
            console.error('Indicators not loaded after maximum retries');
            return;
          }
          console.warn(`Indicators data not ready, retrying (attempt ${retryCount}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 500));
          continue;
        }
        
        // Both schema and indicators data are ready
        this.schemaData = schema;
        this.newSchemaData = indicators;
        this.schema$.next(schema);
        this.indicators$.next(indicators);
        
        console.log("Schema Data:", this.schemaData);
        console.log("Indicators Data:", this.newSchemaData);

        this.prepareFormGroup();
        
        // Only call newPrepare if indicators data has Declaration property
        if (this.newSchemaData && this.newSchemaData['Declaration']) {
          this.newPrepare('Declaration');
        }
        
        return; // Success, exit the retry loop
      } catch (error) {
        retryCount++;
        console.error(`Schema loading attempt ${retryCount} failed:`, error);
        
        if (retryCount >= maxRetries) {
          console.error('Failed to load schema after maximum retries');
          return;
        }
        
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  }

  newPrepare(type: string, forceProfileLevel?: string, retryCount: number = 0): void {
    this.selectedIndicatorType = type;
    const maxRetries = 5;
    
    // If schema data is not ready, defer the call with retry limit
    if (!this.newSchemaData || Object.keys(this.newSchemaData).length === 0) {
      if (retryCount < maxRetries) {
        console.warn(`Schema data not ready, retrying newPrepare for ${type} (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(() => this.newPrepare(type, forceProfileLevel, retryCount + 1), 500);
      } else {
        console.error(`Schema data failed to load after ${maxRetries} attempts for ${type}`);
      }
      return;
    }
    
    if (!this.newSchemaData[this.selectedIndicatorType]) {
      if (retryCount < maxRetries) {
        console.warn(`Schema data not available for ${type}, retrying (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(() => this.newPrepare(type, forceProfileLevel, retryCount + 1), 500);
      } else {
        console.error(`Schema data for ${type} failed to load after ${maxRetries} attempts`);
        this.indicatorsList = [];
        this.listofSelectedIndicators = [];
      }
      return;
    }
    
    let indicator = _.keysIn(this.newSchemaData[this.selectedIndicatorType]);
    
    // Get profile level from parameter or form
    const profileLevel = forceProfileLevel || this.formFields?.get('headerProfile')?.get('profileLevel')?.value;
    
    // Filter indicators based on profile level selection, if profile level is set
    if (profileLevel) {
      const level = profileLevel.toLowerCase();
      console.log('indicator:::::::::', indicator, 'profileLevel:', profileLevel);
      indicator = indicator.filter(ind => {
        const indLower = ind.toLowerCase();
        return level === 'item' ? indLower.includes('item') : level === 'declaration' ? indLower.includes('declaration') || indLower.includes('main') : indicator;
      });

      // For 'both' or any other value, keep all indicators
    } else {
      // No profile level set, show all indicators
      console.log('No profile level set, showing all indicators:', indicator);
    }
    
    this.indicatorsList = _.uniq(_.map(indicator, n => _.startCase(n)));
    
    // Only clear selected indicators if this is a profile level change
    if (forceProfileLevel) {
      this.listofSelectedIndicators = [];
    }
  }

  setSelectedIndicators(selectedIndicators: string[]) {
    this.listofSelectedIndicators = [...selectedIndicators];
  }

  // Method to refresh indicators when profile level changes
  refreshIndicatorsForProfileLevel(profileLevel?: string): void {
    if (this.selectedIndicatorType) {
      this.newPrepare(this.selectedIndicatorType, profileLevel);
      // Clear selected indicators and transfer data when profile level changes
      this.listofSelectedIndicators = [];
      this.listOfIndicators = [];
      this.buisnessRuleValue = [];
      (this.detailsFormGroup.get('businessRule') as UntypedFormArray).clear();
      //(this.detailsFormGroup.get('businessRule') as UntypedFormArray).push(this.addNewAttrRowForBusinessRile());
    }
  }

  // Method to get current profile level
  getCurrentProfileLevel(): string | null {
    return this.formFields?.get('headerProfile')?.get('profileLevel')?.value || null;
  }

  prepareFormGroup() {
    const clazzData = [];
    for (const clazz in this.schemaData) {
      if (clazz == "headerProfile") {
        for (const field in this.schemaData[clazz]) {
          if (field) {
            const ob1: any = {};
            ob1[clazz] = this.schemaData[clazz];
            clazzData.push(ob1);
          }
        }
      }
    }
    clazzData.forEach((fields) => {
      for (const field in fields) {
        if (field) {
          this.formFields.addControl(
            field,
            this.fb.group(this.prepareFormControl(fields[field]))
          );
        }
      }
    });
    this.formFields$.next(true);
    return this.formFields;
  }

  public formFieldsPrepared() {
    return this.formFields$.asObservable();
  }

  prepareFormControl(attributes: any) {
    const formGroupConfig: any = {};
    for (const attribute in attributes) {
      // Special handling for reasonForAmendment - only required in amend mode
      if (attribute === 'reasonForAmendment' || attribute === 'amendmentRemarks') {
        formGroupConfig[attribute] = new UntypedFormControl("", []);
        continue;
      }
      if (attribute === 'otherAmendReason') {
        formGroupConfig[attribute] = new UntypedFormControl("", []);
        continue;
      }
      
      if (
        !_.isEmpty(attributes[attribute]["validations"]) &&
        !_.isEmpty(attributes[attribute]["validations"]["pattern"]) &&
        attributes[attribute]["type"] != "Date"
      ) {
        if (attributes[attribute]["mandatory"] == true) {
          formGroupConfig[attribute] = new UntypedFormControl("", [
            Validators.required,
            Validators.pattern(attributes[attribute]["validations"]["pattern"]),
          ]);

          if (attributes[attribute]["validations"].disabled) {
            formGroupConfig[attribute].disable(true);
          }
        } else
          formGroupConfig[attribute] = new UntypedFormControl("", [
            Validators.pattern(attributes[attribute]["validations"]["pattern"]),
          ]);
      } else {
        if (attributes[attribute]["mandatory"] == true)
          formGroupConfig[attribute] = new UntypedFormControl("", [
            Validators.required,
          ]);
        else formGroupConfig[attribute] = new UntypedFormControl("", []);
      }
    }
    formGroupConfig["id"] = new UntypedFormControl("", []);
    formGroupConfig["validPeriodRange"] = new UntypedFormControl("", [
      Validators.required,
    ]);
    formGroupConfig["profileUsage"] = new UntypedFormControl("", [
      Validators.required,
    ]);
    formGroupConfig["otherAmendReason"] = new UntypedFormControl("", []);
    return formGroupConfig;
  }

  assignIndicatorsToTransfer(riskIndicators: any, updateIndicators?: boolean) {
    if (
      _.difference(riskIndicators, this.listofSelectedIndicators).length > 0
    ) {
      _.difference(riskIndicators, this.listofSelectedIndicators).forEach(
        (indicator) => {
          this.fillIndicators(indicator);
        }
      );
    } else if (updateIndicators) {
      riskIndicators.forEach((indicator: any) => {
        this.fillIndicators(indicator);
      });
    } else {
      let dummyData: any = [];
      riskIndicators.forEach((element: any) => {
        const temp = _.filter(this.listOfIndicators, {
          indicatorName: _.camelCase(element),
        });
        dummyData = _.concat(dummyData, temp);
      });
      this.listOfIndicators = dummyData;
    }
    const newArray = this.listOfIndicators.filter((elem, i, arr) => {
      if (arr.indexOf(elem) === i) {
        return elem;
      }
    });
    this.listOfIndicators = [...newArray];
  }

  fillIndicators(item: any) {
    if (this.selectedIndicatorType) {
      this.setAllIndicators(this.newSchemaData[this.selectedIndicatorType]);
      const temp = _.map(
        this.newSchemaData[this.selectedIndicatorType][_.camelCase(item)],
        (value) => {
          return {
            key: value.id,
            selected: false,
            title: _.startCase(
              this.removeTransportDocumentInfo(value.attributeDescription)
            ),
            refTypeCode: value.refTypeCode ? value.refTypeCode : null,
            val: `${_.camelCase(item)}.${value.attribute}`,
            direction: "left",
            controlType: _.lowerCase(value.controlType),
            type: _.lowerCase(value.type),
            targetField: value.attribute,
            indicatorName: _.camelCase(item),
            metaData: value.metaData ?? value.metadata ?? null,
          };
        }
      );
      this.listOfIndicators = _.concat(this.listOfIndicators, temp);
    }
  }

  setAllIndicators(data: any) {
    const flattenedArray: any = _.flatMap(data);
    const formatObject = (value: any, parentKey: any, item: any) => ({
      key: value.id,
      selected: false,
      title: _.startCase(
        this.removeTransportDocumentInfo(value?.attributeDescription)
      ),
      refTypeCode: value.refTypeCode ? value.refTypeCode : null,
      val: `${parentKey}.${value.attribute}`,
      direction: "left",
      controlType: _.lowerCase(value.controlType),
      type: _.lowerCase(value.type),
      targetField: value.attribute,
      indicatorName: _.camelCase(item),
      metaData: value.metaData ?? value.metadata ?? null,
    });

    const formattedArray = flattenedArray.map((item: any) => {
      const parentKey = Object.keys(data).find((key) =>
        data[key].includes(item)
      );
      const { category, attribute } = item;
      return formatObject(item, parentKey, attribute);
    });

    this.listOfAllIndicators = [...formattedArray];
  }

  removeTransportDocumentInfo(inputString: string) {
    const regex = /transportDocumentInfo./g;
    return inputString.replace(regex, "").trim();
  }

  // Method to set conditional validation for reasonForAmendment based on amend mode
  setReasonForAmendmentValidation(isAmendMode: boolean): void {
    const reasonForAmendmentControl = this.formFields?.get('headerProfile')?.get('reasonForAmendment');
    if (reasonForAmendmentControl) {
      isAmendMode ? reasonForAmendmentControl.setValidators([Validators.required]) : reasonForAmendmentControl.clearValidators();
      reasonForAmendmentControl.updateValueAndValidity();
    }
    const amendmentRemarksControl = this.formFields?.get('headerProfile')?.get('amendmentRemarks');
    if (amendmentRemarksControl) {
      isAmendMode ? amendmentRemarksControl.setValidators([Validators.required]) : amendmentRemarksControl.clearValidators();
      amendmentRemarksControl.updateValueAndValidity();
    }
  }

  addNewAttrRow(val?: any, detailId?: any): UntypedFormGroup {
    if (val && val?.value) {
      if (val?.value.indexOf(",") != "-1") {
        val.value = val.value.split(",");
      } else if (val.operator === "in") {
        val.value = [val.value];
      }
    }
    
    return this.fb.group({
      sequence: val?.sequence,
      attributeName: [val && val.attributeName ? val.attributeName : null, [Validators.required]],
      isExpression: [ val && val.isExpression ? val.isExpression : false, [Validators.required]],
      operator: [ val && val.operator ? val.operator : null, [Validators.required]],
      value: [val && val?.value, [Validators.required]],
      logicalOperator: [ val && val.logicalOperator ? val.logicalOperator : null, [Validators.required]],
      openBraces: [val && val?.openBraces ? val?.openBraces : null],
      closeBraces: [val && val?.closeBraces ? val?.closeBraces : null],
      targetField: [val && val.targetField ? val.targetField : null],
      ruleId: [val && val.ruleId ? val.ruleId : null],
      detailId: [detailId ? detailId : null],
      metaData: [val ? val?.metaData : null],
    });
  }

  addNewTreatmentAttrRow(val?: any, treatmentActionData?: any[]): UntypedFormGroup {
    let actionsData: any[] = [];
    
    // Handle actions from treatmentActionData parameter (for updates)
    if (treatmentActionData && Array.isArray(treatmentActionData)) {
      actionsData = treatmentActionData.map((action: any) => ({
        actionId: action.treatmentActionId || action.actionId || null,
        recommendedActionId: action.recommendedActionId || null,
        actionType: action.actionType || '',
        actionSource: action.actionSource || '',
        actionableField: action.actionableObject || action.actionableField || '',
        recommendedAction: action.recommendedAction || ''
      }));
    }
    // Handle actions from val.treatmentActions (for existing data)
    else if (val && val.treatmentActions && Array.isArray(val.treatmentActions)) {
      actionsData = val.treatmentActions.map((action: any) => ({
        actionId: action.treatmentActionId || action.actionId || null,
        recommendedActionId: action.recommendedActionId || null,
        actionType: action.actionType || '',
        actionSource: action.actionSource || '',
        actionableField: action.actionableObject || action.actionableField || '',
        recommendedAction: action.recommendedAction || ''
      }));
    }
    
    return this.fb.group({
      treatmentTypeId: [val?.treatmentTypeId || null],
      recommendedTreatment: [val?.recommendedTreatment || null, [Validators.required]],
      score: [val?.score || null, [Validators.required]],
      departmentCode: [val?.departmentCode || null, [Validators.required]],
      threshold: [val?.threshold || null],
      actions: [actionsData]
    });
  }

  treatmentTypeChange(value: string): void {
    const [item] = this.rdmService
      .getRefValues("kc-profile-treatment-department")
      .filter((item: any) => item.code == value);
      //debugger;
    if (item) {
      const frmArray = this.treatmentForm.get("treatmentItems") as UntypedFormArray;
      frmArray.clear();
      frmArray.push(this.addNewTreatmentAttrRow());

      this.clintService
        .getRdmParentRefType(item.id)
        .subscribe((result: any) => {
          if (this.profileType == "Secret" && value == "PT01") {
            this.department = result.content.filter((item: RefValue) => item.code === "INDPT");
          } else {
            this.department = result.content;
          }
        });
    }
  }

  resetModelForms() {
    if (this.formFields && this.formFields.controls.headerProfile)
      this.formFields.controls.headerProfile.reset();
    this.listOfIndicators = [];
    this.listofSelectedIndicators = [];
    this.treatmentForm.reset();
    this.detailsFormGroup.reset();
    (this.detailsFormGroup.get("businessRule") as UntypedFormArray).clear();
    (this.treatmentForm.get("treatmentItems") as UntypedFormArray).clear();
    this.schedulerForm.reset();
    this.selectedAeoData = [];
    this.buisnessRuleValue = [];
    this.treatmentActionData = [];
  }

  getBusinessRuleIndicatorSchemaProperty(
    indicator: string,
    attribute: string
  ): BusinessRuleIndicatorItem {
    let schemaProperty: BusinessRuleIndicatorItem | any;
    const cxatr = attribute.split("_");
    if (
      !_.isEmpty(
        this.newSchemaData[this.selectedIndicatorType][indicator][cxatr[0]]
      )
    ) {
      schemaProperty =
        this.newSchemaData[this.selectedIndicatorType][indicator][cxatr[0]];
    }
    return schemaProperty;
  }

  get businessRule(): any {
    return this.detailsFormGroup.controls["businessRule"] as UntypedFormArray;
  }

  addRule() {
    // Mark all existing controls as touched to trigger validation display
    this.businessRule.controls.forEach((control: any) => {
      control.markAllAsTouched();
    });
    
    this.buisnessRuleValue.push({
      sequenceNumber: this.buisnessRuleValue.length,
      attributeName: "string",
      isExpression: false,
      controlType: "input",
      refTypeCode: null,
      multipleCondition: "default",
      operatorType: "string",
      openBraces: null,
      closeBraces: null,
      metaData: null
    });
    this.businessRule.controls.forEach((element: any, index: number) => {
      if (element.value.logicalOperator === "==") {
        this.businessRule.controls[index].patchValue({
          logicalOperator: null,
        });
      }
    });
    this.businessRule.push(this.addNewAttrRowForBusinessRile());
  }

  addNewAttrRowForBusinessRile(): UntypedFormGroup {
    return this.fb.group({
      sequence: this.businessRule.length,
      attributeName: [null, Validators.required],
      operator: [null, Validators.required],
      value: [null, Validators.required],
      logicalOperator: [null, Validators.required],
      isExpression: [false],
      openBraces: [null],
      closeBraces: [null],
      targetField: [null],
      ruleId: [null],
      detailId: [null],
      favlistIds: [null],
      metaData: [null]
    });
  }

  refreshRules() {
    this.buisnessRuleValue.forEach((rule: any, index: any) => {
      this.buisnessRuleValue[index].sequenceNumber = index;
      this.businessRule.controls[index]?.patchValue({
        sequence: index,
      });
    });
    this.buisnessRuleValue = [...this.buisnessRuleValue];
  }
}
